import {
  hasUserPermission,
  HOUR,
  isRealUserId,
  isTantalim,
  Logger,
  type CompanyData,
  type PermissionType,
  type UserData,
  type UserGetInput,
  type UserGetSuccess,
  type UserId,
  type UserProfileData,
  type UserProfileInput,
  type UserProfileSuccess,
} from '@terros/common'
import { useTantalimQuery } from '../query'
import { profileKey, userKey } from '../keys'
import { type GetHookResponse, type QueryResponseWithoutData } from '../hooks'
import { useFeature } from '../feature'
import { getPlaceholderData } from './util'
import { useUserCache } from './cache'

const logger = new Logger('useUser')

export function useUser(userId?: UserId): GetHookResponse<UserData> {
  logger.verbose('calling useUser', userId)

  const real = isRealUserId(userId)
  const cache = useUserCache()
  const placeholderData = real ? cache.getUser(userId) : getPlaceholderData(userId)

  return useTantalimQuery<UserGetInput, UserGetSuccess, UserData>({
    url: '/user/get',
    queryKey: userKey(userId),
    input: {
      userId,
    },
    convertResponse: ({ user }) => {
      cache.setTinyUser(user)
      return user
    },
    options: {
      enabled: real,
      gcTime: Infinity,
      staleTime: HOUR,
      placeholderData,
    },
  })
}

export type ProfileState = QueryResponseWithoutData & {
  user?: UserProfileData
  company?: CompanyData
  /** checks for permission on any team */
  hasPermission: (permission: PermissionType) => boolean
  isTantalim: boolean
  isAdmin: boolean
  isDemo: boolean
  isSingleUser: boolean
}

export function useProfile(userId?: UserId | undefined, enabled?: boolean): ProfileState {
  logger.verbose('calling useProfile', userId)

  const query = useTantalimQuery<UserProfileInput, UserProfileSuccess, UserProfileSuccess>({
    url: '/user/profile',
    queryKey: profileKey(userId),
    input: {
      userId,
    },
    convertResponse: (data) => data,
    options: {
      enabled,
      gcTime: Infinity,
      staleTime: HOUR,
    },
  })

  const isSingleUserEnabled = useFeature({ featureId: 'Feature:SingleUser', placeholder: false }).isEnabled

  logger.verbose('query.data', query.data)

  const isTerros = isTantalim(query.data?.company)

  function hasPermission(permission: PermissionType): boolean {
    if (!query.data) {
      return false
    }
    return hasUserPermission(query.data.user, permission, isTerros)
  }

  const { data, ...queryWithoutData } = query
  const isAdminValue = hasAdminRole(data)

  const isSingleUserValue = isSingleUserEnabled && data?.company?.maxUsers === 1

  return {
    ...queryWithoutData,
    user: data?.user,
    company: data?.company,
    hasPermission,
    isTantalim: isTerros,
    isAdmin: isAdminValue,
    isDemo: data?.company ? data.company.kind === 'demo' : false,
    isSingleUser: isSingleUserValue,
  }
}

const hasAdminRole = (profile: UserProfileSuccess | undefined): boolean => {
  return Boolean(profile?.user.roles?.find((role) => role.roleType === 'admin'))
}
